import React from 'react';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { AxiosError } from 'axios';
import { AppContext } from '../../../App';
import useGlynt from '../../hooks/useGlynt';
import { PackageValidation } from '../../../types/packager-types';
import { StatsRow, StatsLabel, StatsValue } from '../../common/StatsRow';

interface IValidationSummaryProps {
  validation: PackageValidation;
  packageId: string;
}

// Read-only accuracy summary (detail fetch only): the untriaged headline plus the
// dismissed breakdown by label. Uses the shared stat-row atoms for consistency
// with the train stats popover.
const ValidationSummary: React.FC<IValidationSummaryProps> = ({ validation, packageId }) => {
  const theme = useTheme();
  const glynt = useGlynt();
  const { setToast } = React.useContext(AppContext);
  const { unresolved_count, dismissed } = validation;

  // Dismissed = issues a reviewer already resolved, grouped by the reason they chose
  // — NOT a per-reason breakdown of the unresolved headline above (an untriaged issue
  // has no reason yet). Drop count-0 reasons so we never render confusing zero rows
  // directly under the headline, where they read as "the 6 vanished".
  const dismissedWithCounts = dismissed.filter((reason) => reason.count > 0);

  // The headline count has no per-issue drilldown in the package detail — fetch the
  // dedicated report on demand. Filter to match the headline: the report includes
  // EXCLUDED entries and already-dismissed issues, which the count excludes.
  const reportMutation = useMutation({
    mutationFn: async () => {
      const rows = await (await glynt).packages.validationReport(packageId);
      const unresolved = rows.filter(
        (r) => !r.dismissal_reason && r.delivery_status !== 'EXCLUDED'
      );
      const blob = new Blob([JSON.stringify(unresolved, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      saveAs(blob, `validation-report-${packageId}.json`);
    },
    onError: (error: AxiosError) => {
      const message =
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data
          ? (error.response.data as { message: string }).message
          : 'Sorry, something went wrong opening the validation report.';
      setToast({ variant: 'error', open: true, message });
    },
  });

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: unresolved_count > 0 ? theme.palette.error.main : theme.palette.success.main,
          }}
          data-cy="validation-unresolved-count"
        >
          {unresolved_count > 0
            ? `${unresolved_count.toLocaleString()} ${
                unresolved_count === 1 ? 'issue still needs' : 'issues still need'
              } review`
            : 'No issues need review'}
        </Typography>

        {unresolved_count > 0 && (
          <Button
            size="small"
            color="error"
            onClick={() => reportMutation.mutate()}
            disabled={reportMutation.isPending}
            data-cy="btn-validation-open-report"
            sx={{ flexShrink: 0, textTransform: 'none' }}
            startIcon={
              reportMutation.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
            }
          >
            Open report →
          </Button>
        )}
      </Box>

      {dismissedWithCounts.length > 0 && (
        <Box data-cy="validation-dismissed" sx={{ mt: 1.5 }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              mb: 1,
            }}
          >
            Dismissed
          </Typography>
          {dismissedWithCounts.map((reason) => (
            <StatsRow key={reason.reason}>
              <StatsLabel>{reason.label.toUpperCase()}</StatsLabel>
              <StatsValue>{reason.count.toLocaleString()}</StatsValue>
            </StatsRow>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ValidationSummary;
